import { Avatar, EmptyState, Spinner } from '@nexo/ui';
import { FolderOpen } from 'lucide-react';
import { formatBytes, formatRelative } from '../../lib/format';
import { useChannelFiles } from '../../lib/queries';
import { FileIcon } from './FileIcon';
import { viewer } from '../viewer/viewerStore';
import { useTranslation } from 'react-i18next';

/** Files shared in the current conversation: image grid + document list. */
export function FilesPanel({ channelId }: { channelId: string | undefined }) {
  const { t } = useTranslation();
  if (!channelId) {
    return (
      <EmptyState
        icon={<FolderOpen className="size-7" />}
        title={t('Abre una conversación')}
        description={t('Aquí verás los archivos compartidos en ella.')}
      />
    );
  }
  return <FilesList channelId={channelId} />;
}

function FilesList({ channelId }: { channelId: string }) {
  const files = useChannelFiles(channelId);
  const { t } = useTranslation();
  if (files.isPending) {
    return (
      <div className="flex justify-center py-8 text-muted">
        <Spinner />
      </div>
    );
  }
  const all = files.data ?? [];
  const images = all.filter((f) => f.isImage);
  const documents = all.filter((f) => !f.isImage);
  if (all.length === 0) {
    return (
      <EmptyState
        icon={<FolderOpen className="size-7" />}
        title={t('Sin archivos todavía')}
        description={t('Arrastra archivos al chat o usa el botón + del mensaje para compartirlos.')}
      />
    );
  }

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto p-3">
      {images.length > 0 && (
        <section className="mb-5">
          <h3 className="mb-2 px-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
            {t('Imágenes · {{count}}', { count: images.length })}
          </h3>
          <div className="grid grid-cols-3 gap-1.5">
            {images.map((file) => (
              <button
                type="button"
                key={file.id}
                onClick={() => viewer.open(images, images.indexOf(file))}
                title={file.name}
                className="group aspect-square overflow-hidden rounded-lg bg-surface-2 ring-1 ring-border"
              >
                <img
                  src={file.url}
                  alt={file.name}
                  loading="lazy"
                  className="size-full object-cover transition-transform group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </section>
      )}
      {documents.length > 0 && (
        <section>
          <h3 className="mb-1 px-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
            {t('Documentos · {{count}}', { count: documents.length })}
          </h3>
          {documents.map((file) => (
            <button
              type="button"
              key={file.id}
              onClick={() => viewer.open(documents, documents.indexOf(file))}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
                <FileIcon contentType={file.contentType} name={file.name} className="size-[18px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{file.name}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted">
                  <Avatar
                    id={file.uploadedBy.id}
                    name={file.uploadedBy.displayName}
                    src={file.uploadedBy.avatarUrl}
                    size="xs"
                  />
                  {formatBytes(file.size)} · {formatRelative(file.createdAt)}
                </span>
              </span>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
